/* eslint-disable @typescript-eslint/ban-ts-ignore */
import React, { useCallback, useContext, useState } from "react";

import { Preview } from "@creatomate/preview";
import client from "@culturehq/client";

import { NotificationPreviewMontageDone, NotificationPreviewMontageError, NotificationTranscriptionDone, NotificationTranscriptionError } from "chq";
import { useProfile } from "app/context";
import { useFetch, useNotificationReceivedListener } from "app/hooks";
import { errorService, t } from "app/utils";

import ErrorMessage from "components/common/ErrorMessage";
import notificationsTypes from "constants/notificationsTypes";

import { CreatomateContext } from "../contexts/VideoStudioContext";
import Media from "./tabs/media/Media";
import Text from "./tabs/text/Text";
import Audio from "./tabs/audio/Audio";
import Template from "./tabs/template/Template";
import Thumbnail from "./tabs/thumbnail/Thumbnail";
import Subtitles from "./tabs/subtitle/Subtitles";
import Library from "./tabs/library/Library";
import Json from "./tabs/json/Json";
import { IMAGE_TYPE, VIDEO_TYPE } from "../utils/constants";
import Stories from "./tabs/stories/Stories";
import Details from "./tabs/details/Details";
import { hasBurnSubtitlesOn, loadNewSubtitle } from "./tabs/subtitle/utils";

interface MenuTabsProps {
  preview: Preview;
}

const MenuTabs: React.FC<MenuTabsProps> = ({ preview }) => {
  const { data, handleUpdateData, handleAutosaveDraft } = useContext(CreatomateContext);
  const {
    activeMenuItem,
    mediaType,
    mediaVersionHistory,
    montage,
    originalTrimStart,
    story,
    subtitles
  } = data;

  const { profile } = useProfile();
  const { organization } = profile;
  const [error, setError] = useState<string | undefined>();
  const isTheOriginal = mediaVersionHistory?.length === 0;

  const { fetching, json: { storyQuestions } } = useFetch(
    // @ts-ignore
    () => client.getStoryQuestions({ all: true, for_montage: true }),
    []
  );

  useNotificationReceivedListener(
    useCallback(
      async (
        notification: NotificationPreviewMontageError | NotificationPreviewMontageDone
          | NotificationTranscriptionDone | NotificationTranscriptionError
      ) => {
        const downloadFile = async (url: string) => {
          try {
            const response = await fetch(url);
            const blob = await response.blob();
            const reader = new FileReader();

            reader.onloadend = () => {
              const element = document.createElement("a");
              if (typeof reader.result === "string") {
                const timestamp = Date.now();
                element.href = reader.result;
                element.download = mediaType === IMAGE_TYPE ? `${timestamp}_photo.jpg` : `${timestamp}_video.mp4`;
                document.body.appendChild(element);
                element.click();
              }

              setTimeout(() => {
                document.body.removeChild(element);
              }, 200);
            };
            reader.readAsDataURL(blob);
          } catch (error) {
            errorService.error(error);
          }
        };

        switch (notification.notificationType) {
          case notificationsTypes.PREVIEW_MONTAGE_ERROR: {
            setError(t("Something went wrong, please try again or contact support"));
            errorService.error(`Error with preview montage ${profile.id} - ${story?.id}`);
            handleUpdateData({ loading: false, progress: 0 });
            break;
          }
          case notificationsTypes.PREVIEW_MONTAGE_DONE: {
            const previewMontage = notification as NotificationPreviewMontageDone;
            downloadFile(previewMontage.entity.url);
            handleUpdateData({ loading: false, progress: 0 });
            break;
          }
          case notificationsTypes.TRANSCRIPTION_ERROR: {
            if (!story) break;
            setError(t("Something went wrong when we were generating your captions. Please try again or contact support"));
            errorService.error(`Error generating captions ${profile.id} - ${story.id}`);
            break;
          }
          case notificationsTypes.TRANSCRIPTION_DONE: {
            if (!story) break;
            const transcriptionDone = notification as NotificationTranscriptionDone;
            const { subtitles: newSubtitles } = transcriptionDone.entity;
            if (!newSubtitles?.length) {
              setError(t("Something went wrong when we were generating your captions. Please try again or contact support"));
              errorService.error(`No data in transcription ${profile.id} - ${story.id}`);
              break;
            }
            const { language, sourceLanguage } = newSubtitles[0];
            await loadNewSubtitle(
              newSubtitles,
              subtitles,
              organization.name || "",
              preview,
              originalTrimStart,
              handleAutosaveDraft,
              isTheOriginal,
              language === "zh" ? sourceLanguage : language
            );
            handleAutosaveDraft({ burnSubtitles: hasBurnSubtitlesOn(organization.name) });
            handleUpdateData({ pendingSubtitle: undefined });
            break;
          }
          default:
            break;
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        isTheOriginal,
        mediaType,
        organization.name,
        originalTrimStart,
        preview,
        profile.id,
        story,
        subtitles
      ]
    )
  );

  let tabContent;

  switch (activeMenuItem) {
    case "stories":
      tabContent = <Stories preview={preview} />;
      break;
    case "templates":
      tabContent = <Template preview={preview} />;
      break;
    case "audio":
      tabContent = mediaType === VIDEO_TYPE ? <Audio preview={preview} /> : <></>;
      break;
    case "subtitles":
      tabContent = !montage && mediaType === VIDEO_TYPE ? <Subtitles preview={preview} /> : <></>;
      break;
    case "text":
      tabContent = <Text preview={preview} />;
      break;
    case "media":
      tabContent = <Media preview={preview} />;
      break;
    case "thumbnail":
      tabContent = !montage && <Thumbnail preview={preview} />;
      break;
    case "library":
      tabContent = <Library preview={preview} />;
      break;
    case "json":
      tabContent = <Json preview={preview} />;
      break;
    case "details":
      tabContent = <Details fetching={fetching} storyQuestions={storyQuestions} />;
      break;
    default:
      tabContent = <></>;
      break;
  }

  return (
    <div className="Stories-Preview Stories-Container no-scrollbar">
      {error && <ErrorMessage error={error} />}
      {tabContent}
    </div>
  );
};

export default MenuTabs;
